import React from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { SettingsSection } from "../common/SettingsSection";
import apiFetch from "../util/fetch-utils";
import { toaster } from "evergreen-ui";
import confirm from "../util/components/ConfirmDialog";
import logo from "./analytics-logo.png";
import { GOOGLE_ANALYTICS_TOKEN_REMOVED } from "../../reducers/login";
import Link from "@material-ui/core/Link";

const TokenList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const TokenRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #f8fafc;
`;

const GALogo = styled.img`
    width: 28px;
    height: 28px;
    flex-shrink: 0;
`;

const TokenInfo = styled.div`
    flex: 1;
    min-width: 0;
`;

const TokenName = styled.div`
    font-size: 0.875rem;
    font-weight: 600;
    color: #0f172a;
    font-family: "Inter", system-ui, sans-serif;
`;

const TokenSub = styled.div`
    font-size: 0.75rem;
    color: #64748b;
    font-family: "Inter", system-ui, sans-serif;
`;

const DeleteBtn = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 1px solid #e2e8f0;
    border-radius: 7px;
    background: #fff;
    color: #94a3b8;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 120ms, color 120ms, border-color 120ms;
    &:hover { background: #fef2f2; color: #ef4444; border-color: #fecaca; }
`;

const AddBtn = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 7px 14px;
    background: #6366f1;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 0.8rem;
    font-weight: 600;
    font-family: "Inter", system-ui, sans-serif;
    cursor: pointer;
    transition: background 120ms;
    &:hover { background: #4f46e5; }
`;

const EmptyState = styled.div`
    font-size: 0.875rem;
    color: #94a3b8;
    font-family: "Inter", system-ui, sans-serif;
    padding: 8px 0;
`;

/* trash icon inline SVG */
const TrashIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6"/>
        <path d="M19 6l-1 14H6L5 6"/>
        <path d="M10 11v6M14 11v6"/>
        <path d="M9 6V4h6v2"/>
    </svg>
);

/**
 * A workspace points at a connection by its `tokenName`, never by the connection's document id, and one
 * connection can be feeding `analyticsSettings`, `searchConsoleSettings` or both.
 */
function usesConnection(workspace, tokenName) {
    return workspace.analyticsSettings?.tokenName === tokenName
        || workspace.searchConsoleSettings?.tokenName === tokenName;
}

export const AnalyticsDetails = () => {
    const analyticsTokens = useSelector(s => s.login.userDetails.analyticsTokens);
    const workspaces      = useSelector(s => s.workspaces.data);
    const dispatch        = useDispatch();

    function onNewConnection() {
        window.location.href = "/api/google/authorize";
    }

    function onDeleteToken(token) {
        const workspaceList = workspaces
            .filter(ws => usesConnection(ws, token.name))
            .map(ws => (
                <li key={ws.seqNumber}>
                    <Link href={"#/ws/" + ws.seqNumber}>{ws.name}</Link>
                </li>
            ));

        const WorkspaceListComponent = () => workspaceList.length === 0
            ? <p>Not used in any workspace.</p>
            : <div><p>These workspaces lose their Google Analytics and Search Console settings and have to be connected again:</p><ul>{workspaceList}</ul></div>;

        confirm({
            title: "Remove Google Analytics Connection",
            message: (
                <div>
                    <p>Remove the connection for <strong>{token.analyticsAccountOwner}</strong>?</p>
                    <WorkspaceListComponent />
                    <p>Data already imported stays, and stops being updated.</p>
                </div>
            ),
            confirmText: "Remove",
            confirmColor: "primary",
            cancelColor: "link text-danger",
        }).then(val => { if (val) deleteToken(token); });
    }

    function deleteToken(token) {
        apiFetch(`/api/google/tokens/${token.id}`, {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
        }).then(value => {
            if (value.status === 200) {
                toaster.success("Analytics connection removed");
                dispatch({ type: GOOGLE_ANALYTICS_TOKEN_REMOVED, id: token.id });
            } else {
                toaster.danger("Delete failed", { description: value.status + " " + value.statusText, duration: 10 });
            }
        });
    }

    const addBtn = (
        <AddBtn onClick={onNewConnection}>
            <img src={logo} alt="" width={16} height={16} style={{ borderRadius: 2 }} />
            Add account
        </AddBtn>
    );

    return (
        <SettingsSection title="Google Analytics" cardActions={addBtn}>
            <TokenList>
                {analyticsTokens.length === 0 && (
                    <EmptyState>No connected accounts yet.</EmptyState>
                )}
                {analyticsTokens.map((token, i) => (
                    <TokenRow key={i}>
                        <GALogo src={logo} alt="Google Analytics" />
                        <TokenInfo>
                            <TokenName>{token.analyticsAccountOwner}</TokenName>
                            <TokenSub>GA Web Account</TokenSub>
                        </TokenInfo>
                        <DeleteBtn onClick={() => onDeleteToken(token)} title="Remove connection">
                            <TrashIcon />
                        </DeleteBtn>
                    </TokenRow>
                ))}
            </TokenList>
        </SettingsSection>
    );
};
